<template>
    <div :class="['app-content-builder-dataset'].concat(stateClassNames)">
        <div v-if="datasetCategory.length > 0" class='app-vertical-navigation__menu-item is--active' disabled>
            <svg :class="['element-icon--editor-' + slugify(datasetCategory), 'element-icon']" width="1em" height="1em"><use :href="['/img/icons.svg#icon-editor-' + datasetCategory.toLowerCase().replace(/\s/g, '-')]"></use></svg>
            <h4 class='app-vertical-navigation__menu-item-title'>{{ datasetCategory }}</h4>
        </div>
        <div class="app-vertical-navigation__submenu-item" @click="select()">
            <p class="app-vertical-navigation__submenu-item-title">{{ title }}</p>

            <span v-if="!hasDataObject"
                :class="['element-badge']">
                Not Found
            </span>

            <span v-else-if="hasNoData"
                :class="['element-badge', 'is--warning']">
                Unsaved
            </span>

            <span v-if="nestedStatus"
                :class="['element-badge', statusClassName]">
                {{ statusLabel }}
            </span>
        </div>
        <div v-if="virtual" class="app-content-builder-dataset__virtual">
            <div class="app-search">
                <div class="input text">
                    <input
                    type="search"
                    v-model="inputValue"
                    @search="search()"
                    placeholder="Search">
                </div>
                <button
                    class="element-button--secondary element-button"
                    type="button"
                    @click="addItem()">
                    Create New
                </button>
            </div>
            <datalist :id="`children-${id}`">
                <option v-for="item in sortedResults" :key="`data-${item.id}`" :value="item.title"></option>
            </datalist>
            <div ref="children" class="app-content-builder-dataset__virtual-list">
                <content-builder-data-object
                    v-for="item in sortedResults"
                    class="app-content-builder-data-object--static app-vertical-navigation__submenu-item"
                    :id="item.id"
                    :name="item.name"
                    :title="item.title"
                    :status="item.status"
                    :key="`page-item-${item.id}`">
                </content-builder-data-object>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'
import ContentBuilderDataObject from './ContentBuilderDataObject'
import { uuid } from '../helpers/util'
import changeSetMixin from '../mixins/change-set'

export default {
    mixins: [changeSetMixin],
    components: {
        ContentBuilderDataObject
    },
    props: {
        id: {
            type: Number
        },
        virtual: {
            type: String,
            default: null
        },
        schemaRef: {
            type: String
        },
        name: {
            type: String
        },
        title: {
            type: String
        }
    },
    data () {
        return {
            isOpen: false,
            inputValue: '',
            query: '',
            datasetCategory: ''
        }
    },
    computed: {
        ...mapState('builder', [
            'selectedId',
            'dataObjects',
            'previousDatasetCategory'
        ]),
        ...mapGetters('builder', [
            'statusMap',
            'manifest'
        ]),
        hasDataObject () {
            return !!this.id
        },
        hasNoData () {
            const assigned = this.dataObjects.find(item => item.id === this.id)
            return assigned && assigned.data === null ? true : false
        },
        stateClassNames () {
            const result = []
            if (this.virtual) {
                result.push('is--virtual')
                if (!this.isOpen) {
                    result.push('list-group-item-action')
                }
            } else {
                result.push('list-group-item-action')
            }

            if (this.isOpen) {
                result.push('is--active')
            }

            return result
        },
        sortedResults () {
            return _.sortBy(this.results, ['title', 'name'])
        },
        results () {
            if (!this.query.length) {
                return this.assignedDataObjects
            }

            const fields = ['name', 'title']

            const query = this.query.toLowerCase()
            return this.assignedDataObjects.filter(item => {
                // const isSelected = this.$store.state.builder.selectedId === item.id
                return fields.some(field => {
                    return item[field]?.toLowerCase().indexOf(query) >= 0
                })
            })
        },
        hasChanges () {
            return this.assignedDataObjects.some(({ status }) => status)
        },
        datasetType () {
            return this.schemaRef.split('\\').pop().split('/').pop()
        },
        assignedDataObjects () {
            const assigned = this.dataObjects.filter(item => item.model_id === this.id)
            return assigned.map(dataObject => ({
                ...dataObject,
                status: this.statusMap[dataObject.id] ?? false
            }))
        }
    },
    watch: {
        async selectedId (value) {
            const contains = this.assignedDataObjects.find(item => item.id === value)
            if (contains) {
                this.isOpen = true
                await this.$nextTick()
                const el = document.querySelector(`#virtual-${value}`)
                if (el) {
                    this.$refs.children.scrollTo({ behavior: 'smooth', top: el.offsetTop })
                }
            }
        },
        inputValue: _.debounce(function () { this.search() }, 150)
    },
    methods: {
        search () {
            this.query = this.inputValue
        },
        select () {
            if (this.virtual) {
                this.isOpen = !this.isOpen
            } else {
                this.$store.dispatch('builder/selectObject', this.id)
            }
        },
        slugify (str) {
            return str ? _.kebabCase(str) : '' // lowercase with dash in-between
        },
        async addItem () {
            const dataObject = {
                title: `untitled ${this.datasetType}`,
                name: `${this.datasetType}-${uuid(4)}`,
                schema_ref: this.schemaRef,
                is_virtual: true,
                model_id: this.id // set the parent model id
            }

            // add new page
            const result = await this.$store.dispatch('builder/addPage', dataObject)

            // add dataobject as child of this dataset
            this.$store.commit('builder/updateDataObject', {
                id: this.id,
                data_objects: this.assignedDataObjects.concat([result])
            })
        }
    },
    mounted () {
        const currDataset = this.manifest?.datasets?.required.find(dataset => dataset.name === this.name)

        if (currDataset?.category && this.previousDatasetCategory != currDataset.category) {
            this.datasetCategory = currDataset.category
            this.$store.commit('builder/setPreviousDatasetCategory', currDataset.category)
        }
    }
}
</script>
